import React from 'react';

import { Stack, createStyles, Text } from '@mantine/core';
import { useLocalization } from 'context/localization-context';
import { PieChart as RePieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

export type ChartData = {
  label: string;
  value: number;
};
interface IPieChartProps {
  data: ChartData[];
}

export const COLORS = ['#00AFFC', '#4ADEDE', '#797EF6', '#4A6378', '#191970', '#851E9C'];

const RADIAN = Math.PI / 180;

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: 0,
    padding: theme.spacing.md,
    paddingRight: theme.spacing.xl,
    paddingLeft: 0,
    minHeight: 250,
  },
  key: {
    display: 'grid',
    gridTemplateColumns: 'min-content auto min-content',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: theme.spacing.md,
    overflow: 'clip',
    textOverflow: 'ellipsis',
  },
  color: {
    height: 12,
    width: 12,
    borderRadius: theme.radius.xs,
  },
  chartLabel: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    fill: 'white',
  },
}));

const PieChart = ({ data }: IPieChartProps): JSX.Element => {
  const { classes } = useStyles();
  const { formatCurrency } = useLocalization();

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any): JSX.Element => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        className={classes.chartLabel}
        x={x}
        y={y}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline='central'
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className={classes.grid}>
      <ResponsiveContainer width='100%' height='100%'>
        <RePieChart width={400} height={400}>
          <Pie data={data} dataKey={'value'} labelLine={false} label={renderCustomizedLabel}>
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </RePieChart>
      </ResponsiveContainer>

      <Stack spacing='sm'>
        {data.map((p, index) => (
          <div key={`row-${index}`} className={classes.key}>
            <div className={classes.color} style={{ backgroundColor: COLORS[index % 6] }} />
            <Text fw='bold' color='dimmed' truncate>
              {p.label}
            </Text>
            <Text fw='bold'>{formatCurrency(p.value)}</Text>
          </div>
        ))}
      </Stack>
    </div>
  );
};

export default PieChart;
